import React, { useState, useEffect, useCallback, useRef, useMemo, useImperativeHandle, forwardRef } from 'react';
import { Box, Tooltip, Badge } from '@mui/material';
import {
  Settings as SettingsIcon,
  Groups as LobbyIcon,
  LowPriority as LowPriorityIcon,
  Map as MapIcon,
  NearMe as CursorIcon,
  Brush as PaintIcon,
  FiberSmartRecord as TokenIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

// Import extracted components
import CursorToolbar from './tools/CursorToolbar';
import PaintToolbar from './tools/PaintToolbar';
import LobbyDrawer from './drawers/LobbyDrawer';
import InitiativeDrawer from './drawers/InitiativeDrawer';
import MapManagerDrawer from './drawers/MapManagerDrawer';
import {
  ClearGridModal,
  UserNameInputModal,
  ConfirmLeaveModal,
  CoinTransferModal,
  DeleteMapDialog,
} from './shared/GameModals';
import MapEditorModal from './shared/MapEditorModal';
import MonsterInfoModal from './shared/MonsterInfoModal';
import { getMonsterRawByIndex } from '../services/monsterService';
import {
  VerticalSidebar,
  SidebarIcon,
  ModeButton,
  FixedPanel,
} from './shared/StyledComponents';
import SettingsModal from './SettingsModal';
import { EditCharacterDialog, EditHealthDialog, EditStatusDialog } from './shared/CoinDialogs';
import { MODE } from '../constants/gameConstants';
import { useGame } from '../context/GameContext';

const GameSidebar = forwardRef(({ fixedPanelSlot }, ref) => {
  const {
    mode,
    setMode,
    cursorMode,
    setCursorMode,
    selectedCoin,
    selectedObjectId,
    onDeselectCoin,
    onCopyCoin,
    onDeleteCoin,
    onToggleHidden,
    onRemoveFromGrid,
    onEditCoin,
    onEditCoinHealth,
    onEditCoinStatus,
    onRotateObject,
    onDeleteObject,
    paintMode,
    setPaintMode,
    categoryMode,
    setCategoryMode,
    selectedPaintColor,
    onPaintColorSelect,
    onClearColors,
    onClearGrid,
    showClearGrid,
    isCreator,
    canManageCharacters,
    canManageEnemies,
    canPaint,
    roomId,
    setRoomId,
    setUserName,
    permissions,
    connectedUsers,
    gridWidth,
    gridHeight,
    socketHook,
    gridObjectInstances,
    initiativeOrder,
    onInitiativeUpdate,
    maps,
    currentMapId,
    viewingMapId,
    onViewMap,
    onActivateMap,
    showRoomDeletedDialog,
    sessionFlows,
  } = useGame();

  const navigate = useNavigate();
  const isLeavingRoom = useRef(false);

  // Drawer states
  const [isOpen, setIsOpen] = useState(false);
  const [isInitiativeOpen, setIsInitiativeOpen] = useState(false);
  const [isMapManagerOpen, setIsMapManagerOpen] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Map editor modal state
  const [mapEditorOpen, setMapEditorOpen] = useState(false);
  const [mapEditorMode, setMapEditorMode] = useState('create'); // 'create' or 'edit'
  const [editingMap, setEditingMap] = useState(null);

  const [monsterStatBlockOpen, setMonsterStatBlockOpen] = useState(false);
  const [monsterStatBlockCoin, setMonsterStatBlockCoin] = useState(null);

  // Modal states
  const [showEditCharacterDialog, setShowEditCharacterDialog] = useState(false);
  const [showEditHealthDialog, setShowEditHealthDialog] = useState(false);
  const [showEditStatusDialog, setShowEditStatusDialog] = useState(false);
  const [showClearGridModal, setShowClearGridModal] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [joiningRoomId, setJoiningRoomId] = useState(null);

  // Map management states
  const [deleteMapDialogOpen, setDeleteMapDialogOpen] = useState(false);
  const [selectedMapId, setSelectedMapId] = useState(null);
  const [isMapLoading, setIsMapLoading] = useState(false);

  // Coin transfer states
  const [coinTransferModalOpen, setCoinTransferModalOpen] = useState(false);
  const [mapToActivate, setMapToActivate] = useState(null);
  const [sourceMapCoins, setSourceMapCoins] = useState([]);

  // Room states
  const [roomCode, setRoomCode] = useState('');
  const [showJoinFields, setShowJoinFields] = useState(false);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const autoJoinAttemptedRef = useRef(false);

  // Memoized maps
  const memoizedMaps = useMemo(() => maps || [], [maps]);

  const handleOpenMonsterStatBlock = useCallback((coin) => {
    if (!coin) return;
    setMonsterStatBlockCoin(coin);
    setMonsterStatBlockOpen(true);
  }, []);

  const handleCloseMonsterStatBlock = useCallback(() => {
    setMonsterStatBlockOpen(false);
    setMonsterStatBlockCoin(null);
  }, []);

  // Check for room code in URL on mount and handle auto-join
  useEffect(() => {
    // Skip if already in a room or already attempted auto-join
    if (roomId || autoJoinAttemptedRef.current) {
      return;
    }
    
    const params = new URLSearchParams(window.location.search);
    const roomFromUrl = params.get('room');
    const autoJoin = params.get('autoJoin') === 'true';
    const playerName = params.get('playerName');
    const isPresenter = params.get('isPresenter') === 'true';
    
    if (!roomFromUrl) {
      return;
    }
    
    // If auto-join params are present, do auto-join directly (don't set joiningRoomId)
    if (autoJoin && playerName) {
      // Mark as attempted immediately to prevent re-runs
      autoJoinAttemptedRef.current = true;
      
      socketHook.joinRoom(roomFromUrl)
        .then((response) => {
          if (response.success) {
            setUserName(playerName);
            setRoomId(roomFromUrl);
            sessionFlows.applyJoinRoomResponse(response);
            // Add user with presenter flag
            socketHook.addUser(playerName, isPresenter);
          }
        })
        .catch((error) => {
          console.error('Auto-join error:', error);
          autoJoinAttemptedRef.current = false; // Allow retry on error
          navigate('/');
        });
    } else {
      // Normal flow - show the name input modal
      setJoiningRoomId(roomFromUrl);
    }
  }, [roomId, socketHook, setUserName, setRoomId, sessionFlows, navigate]);

  // Room management handlers
  const handleCreateRoom = useCallback(() => {
    setIsCreatingRoom(true);
    setShowSettingsModal(true);
  }, []);

  const handleJoinRoom = useCallback(() => {
    if (roomCode) {
      setJoiningRoomId(roomCode);
      setShowJoinFields(false);
    }
  }, [roomCode]);

  const handleSaveSettings = useCallback(async (width, height, newPermissions, name, addPresenter = false) => {
    try {
      await sessionFlows.saveSettings({
        width,
        height,
        newPermissions,
        name,
        addPresenter,
        isCreatingRoom,
      });
      setShowSettingsModal(false);
      setIsCreatingRoom(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings: ' + error.message);
      setIsCreatingRoom(false);
    }
  }, [isCreatingRoom, sessionFlows]);

  const handleSettingsClick = useCallback(() => {
    setShowSettingsModal(true);
  }, []);

  const copyRoomLink = useCallback(() => {
    if (roomId) {
      navigator.clipboard.writeText(`${window.location.origin}?room=${roomId}`);
    }
  }, [roomId]);

  const confirmLeaveRoom = useCallback(() => {
    isLeavingRoom.current = true;
    sessionFlows.leaveRoom();
    setShowLeaveConfirm(false);
    setJoiningRoomId(null);
  }, [sessionFlows]);

  // Map management handlers
  const handleOpenCreateMap = useCallback(() => {
    setMapEditorMode('create');
    setEditingMap(null);
    setMapEditorOpen(true);
  }, []);

  const handleOpenEditMap = useCallback((map) => {
    setMapEditorMode('edit');
    setEditingMap(map);
    setMapEditorOpen(true);
  }, []);

  const handleOpenMapEditorForCurrentMap = useCallback(() => {
    if (roomId && !isCreator) {
      alert('Only the room creator can edit maps');
      return;
    }
    const mapId = viewingMapId ?? currentMapId;
    const map = memoizedMaps.find((m) => m.id === mapId);
    if (!map) {
      alert('No map to edit.');
      return;
    }
    setIsOpen(false);
    setIsInitiativeOpen(false);
    setIsMapManagerOpen(false);
    handleOpenEditMap(map);
  }, [roomId, isCreator, currentMapId, viewingMapId, memoizedMaps, handleOpenEditMap]);

  const handleMapEditorSave = useCallback(async (mapData) => {
    try {
      setIsMapLoading(true);
      await sessionFlows.saveMapEditor({
        mapEditorMode,
        mapData,
        editingMap,
      });
      
      setMapEditorOpen(false);
      setEditingMap(null);
    } catch (error) {
      console.error('Error saving map:', error);
      alert('Failed to save map: ' + error.message);
    } finally {
      setIsMapLoading(false);
    }
  }, [mapEditorMode, editingMap, sessionFlows]);

  const handleDuplicateMap = useCallback(async (mapId) => {
    try {
      setIsMapLoading(true);
      await sessionFlows.duplicateMap(mapId);
    } catch (error) {
      console.error('Error duplicating map:', error);
      alert('Failed to duplicate map: ' + error.message);
    } finally {
      setIsMapLoading(false);
    }
  }, [sessionFlows]);


  const handleDeleteMap = useCallback(async () => {
    if (!selectedMapId) return;
    
    try {
      setIsMapLoading(true);
      await sessionFlows.deleteMap(selectedMapId);
      setDeleteMapDialogOpen(false);
      setSelectedMapId(null);
    } catch (error) {
      console.error('Error deleting map:', error);
      alert('Failed to delete map: ' + error.message);
    } finally {
      setIsMapLoading(false);
    }
  }, [selectedMapId, sessionFlows]);

  const openDeleteDialog = useCallback((mapId) => {
    setSelectedMapId(mapId);
    setDeleteMapDialogOpen(true);
  }, []);

  const handleMapActivation = useCallback((mapId) => {
    if (currentMapId === mapId) return;
    
    const currentActiveMap = memoizedMaps.find(m => m.id === currentMapId);
    
    if (currentActiveMap && currentActiveMap.coins && currentActiveMap.coins.length > 0) {
      setSourceMapCoins(currentActiveMap.coins);
      setMapToActivate(mapId);
      setCoinTransferModalOpen(true);
    } else {
      onActivateMap(mapId);
    }
  }, [currentMapId, memoizedMaps, onActivateMap]);

  useImperativeHandle(ref, () => ({
    requestMapActivation: handleMapActivation,
    openMapManager: () => {
      setIsOpen(false);
      setIsInitiativeOpen(false);
      setIsMapManagerOpen(true);
    },
    openMapEditorForCurrentMap: handleOpenMapEditorForCurrentMap,
  }), [handleMapActivation, handleOpenMapEditorForCurrentMap]);

  const handleCoinTransferConfirm = useCallback((selectedCoins) => {
    if (!mapToActivate) return;
    
    setCoinTransferModalOpen(false);
    
    if (selectedCoins.length === 0) {
      onActivateMap(mapToActivate);
      return;
    }
    
    const coinsCopy = selectedCoins.map(coin => ({
      ...coin,
      id: uuidv4(),
      x: undefined,
      y: undefined,
      initiative: undefined
    }));
    
    onActivateMap(mapToActivate, coinsCopy);
    
    setMapToActivate(null);
    setSourceMapCoins([]);
  }, [mapToActivate, onActivateMap]);

  // Drawer toggle helpers
  const openLobby = useCallback(() => {
    setIsInitiativeOpen(false);
    setIsMapManagerOpen(false);
    setIsOpen(!isOpen);
  }, [isOpen]);

  const openInitiative = useCallback(() => {
    setIsOpen(false);
    setIsMapManagerOpen(false);
    setIsInitiativeOpen(!isInitiativeOpen);
  }, [isInitiativeOpen]);

  const openMapManager = useCallback(() => {
    setIsOpen(false);
    setIsInitiativeOpen(false);
    setIsMapManagerOpen(!isMapManagerOpen);
  }, [isMapManagerOpen]);

  const hasFixedPanel = Boolean(fixedPanelSlot);

  return (
    <Box sx={{ '--game-sidebar-content-left': hasFixedPanel ? '336px' : '56px' }}>
      {/* Vertical Sidebar */}
      <VerticalSidebar elevation={3}>
        {/* Mode buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Tooltip title="Cursor" placement="right">
            <ModeButton 
              active={mode === MODE.CURSOR} 
              onClick={() => setMode(MODE.CURSOR)}
            >
              <CursorIcon />
            </ModeButton>
          </Tooltip>

          {canPaint && (
            <Tooltip title="Draw" placement="right">
              <ModeButton 
                active={mode === MODE.PAINT} 
                onClick={() => {
                  setMode(MODE.PAINT);
                  onDeselectCoin?.();
                }}
              >
                <PaintIcon />
              </ModeButton>
            </Tooltip>
          )}

          {canManageCharacters && (
            <Tooltip title="Tokens" placement="right">
              <ModeButton 
                active={mode === MODE.TOKEN} 
                onClick={() => {
                  setMode(MODE.TOKEN);
                  onDeselectCoin?.();
                }}
              >
                <TokenIcon />
              </ModeButton>
            </Tooltip>
          )}
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {(!roomId || isCreator) && (
            <Tooltip title="Map Manager" placement="right">
              <SidebarIcon onClick={openMapManager} active={isMapManagerOpen}>
                <MapIcon />
              </SidebarIcon>
            </Tooltip>
          )}

        {/* Drawer toggle icons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', pb: 2 }}>
          <Tooltip title="Lobby" placement="right">
            <SidebarIcon onClick={openLobby} active={isOpen}>
              <Badge badgeContent={connectedUsers.length} color="primary">
                <LobbyIcon />
              </Badge>
            </SidebarIcon>
          </Tooltip>

          <Tooltip title="Turn Order" placement="right">
            <SidebarIcon onClick={openInitiative} active={isInitiativeOpen}>
              <LowPriorityIcon />
            </SidebarIcon>
          </Tooltip>


          {/* Settings */}
          {isCreator && (
            <Tooltip title="Settings" placement="right">
              <SidebarIcon onClick={handleSettingsClick} active={showSettingsModal}>
                <SettingsIcon />
              </SidebarIcon>
            </Tooltip>
          )}

         
        </Box>
      </VerticalSidebar>

      {/* Fixed Panel */}
      {hasFixedPanel && (
        <FixedPanel>
          {fixedPanelSlot}
        </FixedPanel>
      )}

      {/* Contextual Toolbars */}
      {(mode === MODE.CURSOR || mode === MODE.TOKEN) && (
        <CursorToolbar
          cursorMode={cursorMode}
          canPaint={canPaint}
          onCursorModeChange={setCursorMode}
          selectedCoin={selectedCoin}
          selectedObject={selectedObjectId ? gridObjectInstances[selectedObjectId] : null}
          onEditCharacter={() => setShowEditCharacterDialog(true)}
          onEditHealth={() => setShowEditHealthDialog(true)}
          onEditStatus={() => setShowEditStatusDialog(true)}
          onDeselectCoin={onDeselectCoin}
          onCopyCoin={onCopyCoin}
          onDeleteCoin={onDeleteCoin}
          onToggleHidden={onToggleHidden}
          onRemoveFromGrid={onRemoveFromGrid}
          onRotateObject={onRotateObject}
          onDeleteObject={onDeleteObject}
          onOpenMonsterStatBlock={handleOpenMonsterStatBlock}
          canManageEnemies={canManageEnemies}
          isTokenMode={mode === MODE.TOKEN}
        />
      )}

      {mode === MODE.PAINT && canPaint && (
        <PaintToolbar
          paintMode={paintMode}
          setPaintMode={setPaintMode}
          categoryMode={categoryMode}
          setCategoryMode={setCategoryMode}
          selectedPaintColor={selectedPaintColor}
          onPaintColorSelect={onPaintColorSelect}
          onClearColors={onClearColors}
          onClearGrid={() => setShowClearGridModal(true)}
          showClearGrid={showClearGrid}
          isCreator={isCreator}
        />
      )}

      {/* Drawers */}
      <LobbyDrawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        roomId={roomId}
        isCreator={isCreator}
        connectedUsers={connectedUsers}
        onCreateRoom={handleCreateRoom}
        onLeaveRoom={() => setShowLeaveConfirm(true)}
        onCopyRoomLink={copyRoomLink}
        roomCode={roomCode}
        setRoomCode={setRoomCode}
        showJoinFields={showJoinFields}
        setShowJoinFields={setShowJoinFields}
        onJoinRoom={handleJoinRoom}
      />

      <InitiativeDrawer
        isOpen={isInitiativeOpen}
        onClose={() => setIsInitiativeOpen(false)}
        initiativeOrder={initiativeOrder}
        onInitiativeUpdate={onInitiativeUpdate}
      />

      <MapManagerDrawer
        isOpen={isMapManagerOpen}
        onClose={() => setIsMapManagerOpen(false)}
        maps={memoizedMaps}
        currentMapId={currentMapId}
        viewingMapId={viewingMapId}
        isCreator={isCreator}
        isMapLoading={isMapLoading}
        onViewMap={onViewMap}
        onCreateMap={handleOpenCreateMap}
        onEditMap={handleOpenEditMap}
        onDuplicateMap={handleDuplicateMap}
        onDeleteMap={openDeleteDialog}
      />

      {/* Modals */}
      {showSettingsModal && !joiningRoomId && (
        <SettingsModal
          currentWidth={gridWidth}
          currentHeight={gridHeight}
          currentPermissions={permissions}
          onSave={handleSaveSettings}
          onClose={() => setShowSettingsModal(false)}
          isCreatingRoom={isCreatingRoom}
          hasRoomId={!!roomId}
          defaultUserName={socketHook.getLastUserName()}
        />
      )}

      {joiningRoomId && !showRoomDeletedDialog && (
        <UserNameInputModal
          open={true}
          defaultName={socketHook.getLastUserName()}
          onClose={() => {
            setJoiningRoomId(null);
            navigate('/');
          }}
          onSubmit={async (name) => {
            try {
              const response = await socketHook.joinRoom(joiningRoomId);
              if (response.success) {
                setUserName(name);
                setRoomId(joiningRoomId);
                sessionFlows.applyJoinRoomResponse(response);
                // NOTE: battlemapId is set by useSocket's joinRoom handler via setSelectedBattlemapId
                // Do NOT call onSelectBattlemap here - it would send an update back to the server!
                socketHook.addUser(name);
                setJoiningRoomId(null);
              }
            } catch (error) {
              console.error('Error joining room:', error);
              alert(error.message);
              navigate('/');
            }
          }}
        />
      )}

      <ConfirmLeaveModal
        open={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={confirmLeaveRoom}
        isCreator={isCreator}
      />

      <CoinTransferModal
        open={coinTransferModalOpen}
        onClose={() => {
          setCoinTransferModalOpen(false);
          setMapToActivate(null);
        }}
        availableCoins={sourceMapCoins}
        onConfirm={handleCoinTransferConfirm}
        sourceMapName={memoizedMaps.find(m => m.id === currentMapId)?.name || 'Current Map'}
        targetMapName={memoizedMaps.find(m => m.id === mapToActivate)?.name || 'New Map'}
      />

      <ClearGridModal
        open={showClearGridModal}
        onClose={() => setShowClearGridModal(false)}
        onConfirm={() => {
          setShowClearGridModal(false);
          onClearGrid?.();
        }}
      />

      <MapEditorModal
        open={mapEditorOpen}
        onClose={() => {
          setMapEditorOpen(false);
          setEditingMap(null);
        }}
        onSave={handleMapEditorSave}
        isLoading={isMapLoading}
        mode={mapEditorMode}
        initialData={editingMap}
      />

      <DeleteMapDialog
        open={deleteMapDialogOpen}
        onClose={() => setDeleteMapDialogOpen(false)}
        onConfirm={handleDeleteMap}
        isLoading={isMapLoading}
      />

      <EditCharacterDialog
        open={showEditCharacterDialog}
        onClose={() => setShowEditCharacterDialog(false)}
        coin={selectedCoin}
        canManageEnemies={canManageEnemies}
        onSave={(name, color, isLarge, isEnemy, armorClass, level, isPc) => {
          onEditCoin?.(selectedCoin?.id, name, color, isLarge, isEnemy, armorClass, level, isPc);
        }}
      />

      <EditHealthDialog
        open={showEditHealthDialog}
        onClose={() => setShowEditHealthDialog(false)}
        coin={selectedCoin}
        onSave={(health) => {
          onEditCoinHealth?.(selectedCoin?.id, health);
        }}
      />

      <EditStatusDialog
        open={showEditStatusDialog}
        onClose={() => setShowEditStatusDialog(false)}
        coin={selectedCoin}
        onSave={(statuses) => {
          onEditCoinStatus?.(selectedCoin?.id, statuses);
        }}
      />

      <MonsterInfoModal
        open={monsterStatBlockOpen}
        onClose={handleCloseMonsterStatBlock}
        rawMonster={
          monsterStatBlockCoin?.monsterData?.index
            ? getMonsterRawByIndex(monsterStatBlockCoin.monsterData.index)
            : null
        }
        coin={monsterStatBlockCoin}
      />
    </Box>
  );
});

GameSidebar.displayName = 'GameSidebar';

export default GameSidebar;

